import { supabase } from "../supabase-client";
import type { Database } from "../types/database";

type Profile = Database["public"]["Tables"]["profiles"]["Row"];
type Document = Database["public"]["Tables"]["documents"]["Row"];

export interface AdminUser {
  id: string;
  role: "super_admin" | "admin" | "moderator";
  permissions: Record<string, boolean>;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  is_active: boolean;
  email?: string;
  full_name?: string;
}

export interface UserWithStats extends Profile {
  document_count: number;
  last_document_date: string | null;
}

export interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  totalDocuments: number;
  documentsToday: number;
  newUsersThisWeek: number;
  totalRevenue: number;
  usersByType: {
    lawyer: number;
    firm: number;
    client: number;
  };
  usersByCountry: Record<string, number>;
}

export interface DocumentWithUser extends Document {
  id: string;
  user_email: string;
  user_name: string;
  user_type: string | null;
  title: string;
  document_type: string;
  country: string;
  status: "draft" | "final";
  created_at: string;
}

export interface ActivityLogEntry {
  id: string;
  admin_id: string | null;
  action: string;
  entity_type: string;
  entity_id: string | null;
  details: Record<string, any>;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  admin_name?: string;
  admin_email?: string;
}

// =====================================================
// ADMIN VERIFICATION
// =====================================================

/**
 * Check if a user is an admin
 */
export async function isUserAdmin(userId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc("is_admin", { user_id: userId });

    if (error) throw error;
    return data === true;
  } catch (error) {
    console.error("Error checking admin status:", error);
    return false;
  }
}

/**
 * Get admin user details
 */
export async function getAdminUser(userId: string): Promise<AdminUser | null> {
  try {
    const { data, error } = await supabase
      .from("admin_users")
      .select("*")
      .eq("id", userId)
      .eq("is_active", true)
      .single();

    if (error) throw error;
    return data as AdminUser;
  } catch (error) {
    console.error("Error fetching admin user:", error);
    return null;
  }
}

/**
 * Get admin role
 */
export async function getAdminRole(userId: string): Promise<string | null> {
  try {
    const { data, error } = await supabase.rpc("get_admin_role", {
      user_id: userId,
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error fetching admin role:", error);
    return null;
  }
}

/**
 * Get user profile with admin status in a single optimized call
 * This combines profile, admin status, and admin role queries for better performance
 */
export interface UserWithAdminStatus {
  profile: Profile;
  isAdmin: boolean;
  adminRole: string | null;
}

export async function getUserWithAdminStatus(
  userId: string
): Promise<UserWithAdminStatus> {
  try {
    // Parallel queries for profile and admin status
    const [profileResult, adminStatusResult] = await Promise.allSettled([
      supabase.from("profiles").select("*").eq("id", userId).single(),
      supabase.rpc("is_admin", { user_id: userId }),
    ]);

    // Handle profile
    let profile: Profile;
    if (profileResult.status === "fulfilled" && profileResult.value.data) {
      profile = profileResult.value.data as Profile;
    } else {
      throw new Error("Failed to load profile");
    }

    // Handle admin status
    let isAdmin = false;
    let adminRole: string | null = null;

    if (
      adminStatusResult.status === "fulfilled" &&
      adminStatusResult.value.data === true
    ) {
      isAdmin = true;
      // Fetch admin role if user is admin
      try {
        const { data: role, error: roleError } = await supabase.rpc(
          "get_admin_role",
          { user_id: userId }
        );

        if (!roleError && role) {
          adminRole = role;
        }
      } catch (error) {
        console.error("Error fetching admin role:", error);
        // Continue with isAdmin = true but adminRole = null
      }
    }

    return {
      profile,
      isAdmin,
      adminRole,
    };
  } catch (error) {
    console.error("Error fetching user with admin status:", error);
    throw error;
  }
}

/**
 * Check if admin has specific permission
 */
export async function hasPermission(
  userId: string,
  permission: string
): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc("has_permission", {
      user_id: userId,
      permission,
    });

    if (error) throw error;
    return data === true;
  } catch (error) {
    console.error("Error checking permission:", error);
    return false;
  }
}

// =====================================================
// USER MANAGEMENT
// =====================================================

export interface GetAllUsersFilters {
  user_type?: "lawyer" | "firm" | "client";
  country?: string;
  plan?: "free" | "pro";
  is_active?: boolean;
  search?: string;
}

export interface PaginationParams {
  page?: number;
  pageSize?: number;
}

/**
 * Get all users with filters and pagination
 */
export async function getAllUsers(
  filters: GetAllUsersFilters = {},
  pagination: PaginationParams = {}
): Promise<{ users: UserWithStats[]; total: number }> {
  try {
    const { page = 1, pageSize = 50 } = pagination;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from("admin_users_overview")
      .select("*", { count: "exact" });

    // Apply filters
    if (filters.user_type) {
      query = query.eq("user_type", filters.user_type);
    }
    if (filters.country) {
      query = query.eq("country", filters.country);
    }
    if (filters.plan) {
      query = query.eq("plan", filters.plan);
    }
    if (filters.is_active !== undefined) {
      query = query.eq("is_active", filters.is_active);
    }
    if (filters.search) {
      query = query.or(
        `email.ilike.%${filters.search}%,full_name.ilike.%${filters.search}%`
      );
    }

    // Apply pagination
    query = query.range(from, to).order("created_at", { ascending: false });

    const { data, error, count } = await query;

    if (error) throw error;

    return {
      users: (data || []) as UserWithStats[],
      total: count || 0,
    };
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
}

/**
 * Get user by ID with full details for admin.
 * Uses admin_users_overview for plan, credits, document_count when available;
 * falls back to profiles if the view does not exist or user not found.
 */
export async function getUserById(userId: string): Promise<Profile | null> {
  try {
    const attachEffectivePlan = async <T extends Record<string, any>>(base: T) => {
      try {
        const { data: subData, error: subError } = await supabase
          .from("subscriptions")
          .select("plan_type, plan_tier, status, trial_end, current_period_end")
          .eq("user_id", userId)
          .in("status", ["active", "trialing"])
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!subError && subData) {
          return {
            ...base,
            effective_plan: subData.plan_type,
            effective_plan_tier: subData.plan_tier,
            effective_subscription_status: subData.status,
            effective_trial_end: subData.trial_end,
            effective_period_end: subData.current_period_end,
          };
        }
      } catch {
        // Ignore and fall back to profile plan
      }

      return {
        ...base,
        effective_plan: (base as any).plan ?? null,
        effective_plan_tier: null,
        effective_subscription_status: null,
        effective_trial_end: null,
        effective_period_end: null,
      };
    };

    const { data: overviewData, error: overviewError } = await supabase
      .from("admin_users_overview")
      .select("*")
      .eq("id", userId)
      .maybeSingle();

    if (!overviewError && overviewData) {
      const withPlan = await attachEffectivePlan(overviewData as Record<string, any>);
      return withPlan as unknown as Profile;
    }

    if (overviewError?.code === "PGRST205") {
      // View doesn't exist, fall back to profiles
    } else if (overviewError) {
      throw overviewError;
    }

    const { data: profileData, error: profileError } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();

    if (profileError) throw profileError;
    const withPlan = await attachEffectivePlan(profileData as Record<string, any>);
    return withPlan as unknown as Profile;
  } catch (error) {
    console.error("Error fetching user:", error);
    return null;
  }
}

/**
 * Suspend a user (set is_active to false)
 */
export async function suspendUser(userId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from("profiles")
      .update({ is_active: false })
      .eq("id", userId);

    if (error) throw error;
  } catch (error) {
    console.error("Error suspending user:", error);
    throw error;
  }
}

/**
 * Activate a user (set is_active to true)
 */
export async function activateUser(userId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from("profiles")
      .update({ is_active: true })
      .eq("id", userId);

    if (error) throw error;
  } catch (error) {
    console.error("Error activating user:", error);
    throw error;
  }
}

/**
 * Update user credits (admin only).
 * Updates user_credits (canonical) and syncs profiles.credits_remaining.
 * Uses admin_set_user_credits RPC for proper source of truth.
 */
export async function updateUserCredits(
  targetUserId: string,
  credits: number,
  adminId: string
): Promise<void> {
  try {
    const { error } = await supabase.rpc("admin_set_user_credits", {
      p_admin_id: adminId,
      p_target_user_id: targetUserId,
      p_credits: Math.max(0, Math.round(credits)),
    });

    if (error) throw error;
  } catch (error) {
    console.error("Error updating user credits:", error);
    throw error;
  }
}

/**
 * Update user plan
 */
export async function updateUserPlan(
  userId: string,
  plan: "free" | "pro"
): Promise<void> {
  try {
    const { error } = await supabase
      .from("profiles")
      .update({ plan })
      .eq("id", userId);

    if (error) throw error;
  } catch (error) {
    console.error("Error updating user plan:", error);
    throw error;
  }
}

// =====================================================
// STATISTICS
// =====================================================

/**
 * Get overall platform statistics.
 * Uses admin_users_overview (excludes admins). Falls back to profiles if view missing.
 * Revenue uses RPC to avoid 404 when subscriptions table doesn't exist.
 */
export async function getUserStats(): Promise<AdminStats> {
  try {
    const overview = "admin_users_overview";
    const { error: probeErr } = await supabase
      .from(overview)
      .select("id", { count: "exact", head: true })
      .limit(1);
    const useOverview =
      probeErr?.code !== "PGRST204" && probeErr?.code !== "42P01";

    const fromSource = useOverview
      ? () => supabase.from(overview)
      : () => supabase.from("profiles");

    const { count: totalUsers } = await fromSource()
      .select("*", { count: "exact", head: true });

    const { count: activeUsers } = await fromSource()
      .select("*", { count: "exact", head: true })
      .eq("is_active", true);

    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    const { count: newUsersThisWeek } = await fromSource()
      .select("*", { count: "exact", head: true })
      .gte("created_at", weekAgo.toISOString());

    const { data: userTypeData } = await fromSource()
      .select("user_type")
      .not("user_type", "is", null);

    const usersByType = { lawyer: 0, firm: 0, client: 0 };
    userTypeData?.forEach((u) => {
      if (u.user_type && u.user_type in usersByType) {
        usersByType[u.user_type as keyof typeof usersByType]++;
      }
    });

    const { data: countryData } = await fromSource()
      .select("country")
      .not("country", "is", null);

    const usersByCountry: Record<string, number> = {};
    countryData?.forEach((u) => {
      if (u.country) {
        usersByCountry[u.country] = (usersByCountry[u.country] || 0) + 1;
      }
    });

    // Documents (from documents table)
    const { count: totalDocuments } = await supabase
      .from("documents")
      .select("*", { count: "exact", head: true });

    const today = new Date().toISOString().split("T")[0];
    const { count: documentsToday } = await supabase
      .from("documents")
      .select("*", { count: "exact", head: true })
      .gte("created_at", `${today}T00:00:00`)
      .lte("created_at", `${today}T23:59:59`);

    // Revenue: use RPC to avoid 404 when subscriptions table doesn't exist
    let totalRevenue = 0;
    try {
      const { data: revenue, error: revErr } = await supabase.rpc(
        "get_admin_revenue_safe"
      );
      if (!revErr && revenue != null)
        totalRevenue = Number(revenue) || 0;
    } catch {
      totalRevenue = 0;
    }

    return {
      totalUsers: totalUsers || 0,
      activeUsers: activeUsers || 0,
      totalDocuments: totalDocuments || 0,
      documentsToday: documentsToday || 0,
      newUsersThisWeek: newUsersThisWeek || 0,
      totalRevenue,
      usersByType,
      usersByCountry,
    };
  } catch (error) {
    console.error("Error fetching user stats:", error);
    throw error;
  }
}

/**
 * Get document statistics
 */
export async function getDocumentStats(): Promise<{
  total: number;
  byCountry: Record<string, number>;
  byType: Record<string, number>;
  byStatus: Record<string, number>;
}> {
  try {
    // Get total
    const { count: total } = await supabase
      .from("documents")
      .select("*", { count: "exact", head: true });

    // Get by country
    const { data: countryData } = await supabase
      .from("documents")
      .select("country");

    const byCountry: Record<string, number> = {};
    countryData?.forEach((doc) => {
      byCountry[doc.country] = (byCountry[doc.country] || 0) + 1;
    });

    // Get by type
    const { data: typeData } = await supabase
      .from("documents")
      .select("document_type");

    const byType: Record<string, number> = {};
    typeData?.forEach((doc) => {
      byType[doc.document_type] = (byType[doc.document_type] || 0) + 1;
    });

    // Get by status
    const { data: statusData } = await supabase
      .from("documents")
      .select("status");

    const byStatus: Record<string, number> = {};
    statusData?.forEach((doc) => {
      byStatus[doc.status] = (byStatus[doc.status] || 0) + 1;
    });

    return {
      total: total || 0,
      byCountry,
      byType,
      byStatus,
    };
  } catch (error) {
    console.error("Error fetching document stats:", error);
    throw error;
  }
}

// =====================================================
// DOCUMENT MANAGEMENT
// =====================================================

export interface GetAllDocumentsFilters {
  country?: string;
  document_type?: string;
  status?: "draft" | "final";
  user_id?: string;
  date_from?: string;
  date_to?: string;
}

/**
 * Get all documents with filters and pagination (admin view)
 */
export async function adminGetAllDocuments(
  filters: GetAllDocumentsFilters = {},
  pagination: PaginationParams = {}
): Promise<{ documents: DocumentWithUser[]; total: number }> {
  try {
    const { page = 1, pageSize = 50 } = pagination;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase.from("documents").select(
      `
        *,
        profiles!inner(
          email,
          full_name,
          user_type
        )
      `,
      { count: "exact" }
    );

    // Apply filters
    if (filters.country) {
      query = query.eq("country", filters.country);
    }
    if (filters.document_type) {
      query = query.eq("document_type", filters.document_type);
    }
    if (filters.status) {
      query = query.eq("status", filters.status);
    }
    if (filters.user_id) {
      query = query.eq("user_id", filters.user_id);
    }
    if (filters.date_from) {
      query = query.gte("created_at", filters.date_from);
    }
    if (filters.date_to) {
      query = query.lte("created_at", filters.date_to);
    }

    // Apply pagination
    query = query.range(from, to).order("created_at", { ascending: false });

    const { data, error, count } = await query;

    if (error) throw error;

    // Transform data to include user info at root level
    const documents =
      data?.map((doc: any) => ({
        ...doc,
        user_email: doc.profiles?.email || "",
        user_name: doc.profiles?.full_name || "",
        user_type: doc.profiles?.user_type || null,
      })) || [];

    return {
      documents: documents as DocumentWithUser[],
      total: count || 0,
    };
  } catch (error) {
    console.error("Error fetching documents:", error);
    throw error;
  }
}

/**
 * Delete a document (admin moderation)
 */
export async function adminDeleteDocument(documentId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from("documents")
      .delete()
      .eq("id", documentId);

    if (error) throw error;
  } catch (error) {
    console.error("Error deleting document:", error);
    throw error;
  }
}

// =====================================================
// ADMIN OPS - CASES & REPORTS
// =====================================================

export interface AdminCaseRow {
  id: string | null;
  case_number: string | null;
  title: string | null;
  lawyer_id: string | null;
  lawyer_name: string | null;
  lawyer_email: string | null;
  client_id: string | null;
  client_name: string | null;
  client_email: string | null;
  status: string | null;
  practice_area: string | null;
  country: string | null;
  created_at: string | null;
  closed_at: string | null;
  document_count: number | null;
  event_count: number | null;
}

/**
 * Get cases for admin (uses cases_with_details view or fallback direct query)
 */
export async function getAdminCases(options?: {
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<AdminCaseRow[]> {
  const limit = options?.limit ?? 100;
  const offset = options?.offset ?? 0;

  // Try view first
  let query = supabase.from("cases_with_details").select("*");
  if (options?.status) query = query.eq("status", options.status);
  query = query.order("created_at", { ascending: false });
  query = query.range(offset, offset + limit - 1);

  const { data: viewData, error: viewError } = await query;

  if (!viewError) {
    return (viewData ?? []) as AdminCaseRow[];
  }

  // Fallback when view doesn't exist (e.g. PGRST205)
  if (viewError.code !== "PGRST205") throw viewError;

  let casesQuery = supabase
    .from("cases")
    .select("id, case_number, title, status, practice_area, country, created_at, closed_at, lawyer_id, client_id")
    .order("created_at", { ascending: false });
  if (options?.status) casesQuery = casesQuery.eq("status", options.status);
  casesQuery = casesQuery.range(offset, offset + limit - 1);

  const { data: casesRows, error: casesErr } = await casesQuery;
  if (casesErr) throw casesErr;

  const cases = casesRows ?? [];
  if (cases.length === 0) return [];

  const lawyerIds = [...new Set(cases.map((c) => c.lawyer_id).filter(Boolean))] as string[];
  const clientIds = [...new Set(cases.map((c) => c.client_id).filter(Boolean))] as string[];
  const caseIds = cases.map((c) => c.id);

  const [lawyerProfilesRes, clientProfilesRes, docCountRes, eventCountRes] = await Promise.all([
    lawyerIds.length
      ? supabase.from("profiles").select("id, full_name, email").in("id", lawyerIds)
      : Promise.resolve({ data: [] }),
    clientIds.length
      ? supabase.from("profiles").select("id, full_name, email").in("id", clientIds)
      : Promise.resolve({ data: [] }),
    caseIds.length
      ? supabase.from("case_documents").select("case_id").in("case_id", caseIds)
      : Promise.resolve({ data: [] }),
    caseIds.length
      ? supabase.from("case_events").select("case_id").in("case_id", caseIds)
      : Promise.resolve({ data: [] }),
  ]);

  const lawyerMap = new Map((lawyerProfilesRes.data ?? []).map((p) => [p.id, p]));
  const clientMap = new Map((clientProfilesRes.data ?? []).map((p) => [p.id, p]));
  const docCountByCase = (docCountRes.data ?? []).reduce(
    (acc, r) => {
      acc[r.case_id] = (acc[r.case_id] ?? 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );
  const eventCountByCase = (eventCountRes.data ?? []).reduce(
    (acc, r) => {
      acc[r.case_id] = (acc[r.case_id] ?? 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  return cases.map((c) => {
    const lawyer = lawyerMap.get(c.lawyer_id);
    const client = clientMap.get(c.client_id);
    return {
      id: c.id,
      case_number: c.case_number,
      title: c.title,
      lawyer_id: c.lawyer_id,
      lawyer_name: lawyer?.full_name ?? null,
      lawyer_email: lawyer?.email ?? null,
      client_id: c.client_id,
      client_name: client?.full_name ?? null,
      client_email: client?.email ?? null,
      status: c.status,
      practice_area: c.practice_area,
      country: c.country,
      created_at: c.created_at,
      closed_at: c.closed_at,
      document_count: docCountByCase[c.id] ?? 0,
      event_count: eventCountByCase[c.id] ?? 0,
    } as AdminCaseRow;
  });
}

/**
 * Get user reports for admin
 */
export async function getAdminUserReports(options?: {
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<any[]> {
  let query = supabase
    .from("user_reports")
    .select("*")
    .order("created_at", { ascending: false });

  if (options?.status) {
    query = query.eq("status", options.status);
  }
  if (options?.limit) {
    query = query.range(
      options?.offset ?? 0,
      (options?.offset ?? 0) + options.limit - 1
    );
  }

  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

// =====================================================
// ACTIVITY LOG
// =====================================================

export interface ActivityLogFilters {
  admin_id?: string;
  entity_type?: string;
  action?: string;
  date_from?: string;
  date_to?: string;
}

/**
 * Log admin activity
 */
export async function logAdminActivity(
  adminId: string,
  action: string,
  entityType: string,
  entityId?: string,
  details?: Record<string, any>
): Promise<void> {
  try {
    const { error } = await supabase.from("admin_activity_log").insert({
      admin_id: adminId,
      action,
      entity_type: entityType,
      entity_id: entityId || null,
      details: details || {},
    });

    if (error) throw error;
  } catch (error) {
    console.error("Error logging activity:", error);
    // Don't throw - activity logging shouldn't break the main flow
  }
}

/**
 * Get activity log with filters
 */
export async function getActivityLog(
  filters: ActivityLogFilters = {},
  pagination: PaginationParams = {}
): Promise<{ activities: ActivityLogEntry[]; total: number }> {
  try {
    const { page = 1, pageSize = 50 } = pagination;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    // Build base query for activity log
    let query = supabase
      .from("admin_activity_log")
      .select("*", { count: "exact" });

    // Apply filters
    if (filters.admin_id) {
      query = query.eq("admin_id", filters.admin_id);
    }
    if (filters.entity_type) {
      query = query.eq("entity_type", filters.entity_type);
    }
    if (filters.action) {
      query = query.eq("action", filters.action);
    }
    if (filters.date_from) {
      query = query.gte("created_at", filters.date_from);
    }
    if (filters.date_to) {
      query = query.lte("created_at", filters.date_to);
    }

    // Apply pagination
    query = query.range(from, to).order("created_at", { ascending: false });

    const { data: activitiesData, error, count } = await query;

    if (error) throw error;

    if (!activitiesData || activitiesData.length === 0) {
      return {
        activities: [],
        total: count || 0,
      };
    }

    // Get unique admin IDs
    const adminIds = [
      ...new Set(activitiesData.map((a) => a.admin_id).filter(Boolean)),
    ];

    // Get profile info for all admin IDs
    let profilesMap = new Map();
    if (adminIds.length > 0) {
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("id, email, full_name")
        .in("id", adminIds as string[]);

      if (!profilesError && profilesData) {
        profilesMap = new Map(
          profilesData.map((profile) => [profile.id, profile])
        );
      }
    }

    // Combine activity and profile data
    const activities = activitiesData.map((activity: any) => {
      const profile = activity.admin_id
        ? profilesMap.get(activity.admin_id)
        : null;
      return {
        ...activity,
        admin_name: profile?.full_name || "Unknown",
        admin_email: profile?.email || "",
      };
    });

    return {
      activities: activities as ActivityLogEntry[],
      total: count || 0,
    };
  } catch (error) {
    console.error("Error fetching activity log:", error);
    throw error;
  }
}

/**
 * Get recent admin activity summary
 */
export async function getAdminActivitySummary(): Promise<any[]> {
  try {
    const { data, error } = await supabase
      .from("admin_activity_summary")
      .select("*")
      .order("last_activity", { ascending: false })
      .limit(10);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error fetching admin activity summary:", error);
    return [];
  }
}

// =====================================================
// ADMIN MANAGEMENT
// =====================================================

/**
 * Search users by email for admin creation.
 * Returns id, email, full_name. Excludes existing admins.
 */
export async function searchAdminCandidateByEmail(
  emailQuery: string
): Promise<{ id: string; email: string; full_name: string }[]> {
  if (!emailQuery || emailQuery.length < 2) return [];

  try {
    const { data: adminIds } = await supabase
      .from("admin_users")
      .select("id")
      .eq("is_active", true);
    const exclude = new Set((adminIds ?? []).map((a: { id: string }) => a.id));

    const { data, error } = await supabase
      .from("profiles")
      .select("id, email, full_name")
      .ilike("email", `%${emailQuery}%`)
      .limit(10);

    if (error) throw error;
    const list = (data ?? []).filter((p: { id: string }) => !exclude.has(p.id));
    return list as { id: string; email: string; full_name: string }[];
  } catch (error) {
    console.error("Error searching admin candidates:", error);
    return [];
  }
}

/**
 * Get all admins
 */
export async function getAllAdmins(): Promise<AdminUser[]> {
  try {
    // First get all admins
    const { data: adminsData, error: adminsError } = await supabase
      .from("admin_users")
      .select("*")
      .eq("is_active", true)
      .order("created_at", { ascending: false });

    if (adminsError) throw adminsError;

    if (!adminsData || adminsData.length === 0) {
      return [];
    }

    // Get profile info for all admin IDs
    const adminIds = adminsData.map((admin) => admin.id);
    const { data: profilesData, error: profilesError } = await supabase
      .from("profiles")
      .select("id, email, full_name")
      .in("id", adminIds);

    if (profilesError) throw profilesError;

    // Create a map of profile data by ID
    const profilesMap = new Map(
      (profilesData || []).map((profile) => [profile.id, profile])
    );

    // Combine admin and profile data
    const admins = adminsData.map((admin) => {
      const profile = profilesMap.get(admin.id);
      return {
        ...admin,
        email: profile?.email || "",
        full_name: profile?.full_name || "",
      };
    });

    return admins as AdminUser[];
  } catch (error) {
    console.error("Error fetching admins:", error);
    throw error;
  }
}

/**
 * Create a new admin (only super_admin can do this)
 */
export async function createAdmin(
  userId: string,
  role: "super_admin" | "admin" | "moderator",
  permissions: Record<string, boolean> = {},
  createdBy: string
): Promise<AdminUser> {
  try {
    const { data, error } = await supabase
      .from("admin_users")
      .insert({
        id: userId,
        role,
        permissions,
        created_by: createdBy,
        is_active: true,
      })
      .select()
      .single();

    if (error) throw error;
    return data as AdminUser;
  } catch (error) {
    console.error("Error creating admin:", error);
    throw error;
  }
}

/**
 * Update admin role and permissions
 */
export async function updateAdminRole(
  adminId: string,
  role: "super_admin" | "admin" | "moderator",
  permissions?: Record<string, boolean>
): Promise<AdminUser> {
  try {
    const updateData: any = { role };
    if (permissions) {
      updateData.permissions = permissions;
    }

    const { data, error } = await supabase
      .from("admin_users")
      .update(updateData)
      .eq("id", adminId)
      .select()
      .single();

    if (error) throw error;
    return data as AdminUser;
  } catch (error) {
    console.error("Error updating admin role:", error);
    throw error;
  }
}

/**
 * Deactivate an admin
 * Guards: cannot deactivate a super_admin
 */
export async function deactivateAdmin(adminId: string): Promise<void> {
  try {
    // Guard: verify the target admin is not a super_admin
    const { data: targetAdmin, error: fetchError } = await supabase
      .from("admin_users")
      .select("role")
      .eq("id", adminId)
      .single();

    if (fetchError) throw fetchError;

    if (targetAdmin?.role === "super_admin") {
      throw new Error("Cannot deactivate a super_admin account.");
    }

    const { error } = await supabase
      .from("admin_users")
      .update({ is_active: false })
      .eq("id", adminId);

    if (error) throw error;
  } catch (error) {
    console.error("Error deactivating admin:", error);
    throw error;
  }
}
